import {defineStore} from 'pinia'
import {BoardsNs, FeatureNs} from "@shared/types";
import {PublicNs} from "@shared/types/public.namespace.ts";
import {PublicPageApi} from "@/api/public-page.apis.ts";
import {usePublicProjectStore} from "@domains/publish/project/stores/public-project.store.ts";
import {setLocalStorage} from "@shared/helpers";


export const usePPFeatureStore = defineStore("ppFeature", {
  state: () => ({
    featureMap: new Map<string, FeatureNs.Feature>,
    featureTree: [] as FeatureNs.FeatureTreeNode[],
    selectedFeature: {} as FeatureNs.Feature | FeatureNs.ProjectPage,
    breadcrumb: [] as BoardsNs.Breadcrumb[],
    isTreeCollapsed: false,
    isSearchModeOn: false,
    projectPageMap: new Map<string, FeatureNs.ProjectPage>,
    projectPageTree: [] as FeatureNs.PageTreeNode[]
  }),
  getters: {
    getFeatureList: (state) => [...state.featureMap.values()],
    getProjectPageList: (state) => [...state.projectPageMap.values()],
    getFeatureTree: (state) => state.featureTree,
    getProjectPageTree: (state) => state.projectPageTree,
    getSelectedFeature: (state) => state.selectedFeature,
    getBreadcrumb: (state) => state.breadcrumb
  },
  actions: {
    convertFlatDataToTree() {
      const map = new Map<string, FeatureNs.FeatureTreeNode>;

      this.featureMap.forEach((item: FeatureNs.Feature, key: string) => {
        map.set(item.id, {...item, ...item.properties, children: []} as FeatureNs.FeatureTreeNode);
      });

      const hierarchy: FeatureNs.FeatureTreeNode[] = [];
      map.forEach((item: FeatureNs.FeatureTreeNode, key: string) => {
        if (item.parentId) {
          const parentItem = map.get(item.parentId);
          if (parentItem) {
            // Find Parent Color
            let parentColor = parentItem.parentColor ?? parentItem.color;
            if (!parentColor) {
              // Move up one step
              if (parentItem.parentId) {
                parentColor = map.get(parentItem.parentId)!.color
              }
            }

            item.parentColor = parentColor;
            parentItem.children.push(item!);
          }
        } else {
          hierarchy.push(item!);
        }
      });

      this.featureTree = hierarchy;
      return hierarchy;
    },


    getFeatureById(featureId: string): FeatureNs.Feature | undefined {
      return this.featureMap.get(featureId);
    },

    async loadAllFeature(projectId: string, token: string, password: string): Promise<FeatureNs.Feature[]> {
      const payload = {projectId, token, password} as PublicNs.LoadProjectDataRequest;
      return PublicPageApi.loadFeatureData(payload).then((featureList) => {
        this.featureMap.clear();
        featureList.forEach(item => {
          this.featureMap.set(item.id, item);
        });
        this.convertFlatDataToTree();
        return featureList
      })
    },

    selectAFeature(payload: FeatureNs.Feature): void {
      if (this.selectedFeature !== payload) {
        this.selectedFeature = payload;

        this.router.push({
          name: PublicNs.PublicRouteEnums.FEATURE_SELECTED,
          params: {featureId: payload.id}
        });

        const newBreadcrumb = [] as BoardsNs.Breadcrumb[]
        this.addToBreadcrumb(payload, newBreadcrumb);
        this.breadcrumb = newBreadcrumb;

        const projectStore = usePublicProjectStore()
        projectStore.addItemToRecentlyViewed({type: 'FEATURE', item: payload});

        setLocalStorage('ppSelectedFeature', payload.id)
      }
    },

    addToBreadcrumb(payload: FeatureNs.Feature | FeatureNs.ProjectPage, newBreadcrumb: BoardsNs.Breadcrumb[]) {
      newBreadcrumb.unshift({
        id: payload.id,
        summary: payload.summary,
        type: payload.featureType
      } as BoardsNs.Breadcrumb)

      if ((<FeatureNs.Feature>payload).parentId) {
        const parent = this.featureMap.get((<FeatureNs.Feature>payload).parentId);
        if (parent !== undefined) {
          this.addToBreadcrumb(parent, newBreadcrumb)
        }
      }
    },

    switchTreeCollapsedValue(): void {
      this.isTreeCollapsed = !this.isTreeCollapsed;
    },

    async loadAllProjectPage(projectId: string, token: string, password: string): Promise<FeatureNs.ProjectPage[]> {
      const payload = {projectId, token, password} as PublicNs.LoadProjectDataRequest;
      return PublicPageApi.loadProjectPageData(payload).then((projectPageList) => {
        this.projectPageMap.clear();
        projectPageList.forEach(item => {
          item.featureType = FeatureNs.FeatureTypeEnums.PAGE
          this.projectPageMap.set(item.id, item);
        });
        this.convertFlatDataToPageTree()
        return projectPageList
      })
    },

    getProjectPageById(pageId: string): FeatureNs.ProjectPage | undefined {
      return this.projectPageMap.get(pageId);
    },

    selectAPage(payload: FeatureNs.ProjectPage): void {
      if (this.selectedFeature?.id !== payload.id) {
        this.selectedFeature = payload;
        this.router.push({
          name: PublicNs.PublicRouteEnums.PAGE_SELECTED,
          params: {pageId: payload.id}
        });

        const newBreadcrumb = [] as BoardsNs.Breadcrumb[]
        this.addToBreadcrumb(payload, newBreadcrumb);
        this.breadcrumb = newBreadcrumb;
      }
    },

    convertFlatDataToPageTree() {
      const hierarchy: FeatureNs.PageTreeNode[] = [];
      this.projectPageMap.forEach((item: FeatureNs.ProjectPage, key: string) => {
        hierarchy.push({...item, ...item.properties, children: []} as FeatureNs.PageTreeNode);
      });

      this.projectPageTree = hierarchy;
      return hierarchy;
    },

    setSearchMode(isSearchModeOn: boolean): void {
      this.isSearchModeOn = isSearchModeOn;
    }


  }

})
